<template>
  <controller :lockScroll="false" :goToTopButton="true">
    <main class="kanban-page">
      <header class="page-header">
        <div>
          <p class="eyebrow">Sucesso das Famílias</p>
          <h1>Mesa de descontos</h1>
          <p>Acompanhe e analise as solicitações de desconto das famílias.</p>
        </div>
        <div class="header-actions">
          <button v-if="canEvaluate" class="button primary" type="button" :disabled="!rule.code" @click="manualRequestOpen = true">
            <fa :icon="['far', 'plus']" aria-hidden="true" />
            Adicionar solicitação
          </button>
          <router-link v-if="canManage" class="button secondary" to="/appscripts/mesa-decisao-descontos/parametros">
            <fa :icon="['far', 'gear']" aria-hidden="true" />
            Parâmetros
          </router-link>
          <span v-if="rule.code" class="rule-badge">Regra {{ rule.code }}</span>
        </div>
      </header>

      <section class="toolbar" aria-label="Filtros do quadro">
        <label for="request-search">Buscar solicitação</label>
        <div class="search-control">
          <fa :icon="['fas', 'magnifying-glass']" aria-hidden="true" />
          <input
            id="request-search"
            v-model.trim="search"
            type="search"
            autocomplete="off"
            placeholder="Aluno, RM ou unidade"
            @input="scheduleLoad"
          >
        </div>
        <button class="button secondary" type="button" :disabled="loading" @click="loadRequests">
          <fa :icon="['fas', 'arrows-rotate']" aria-hidden="true" />
          Atualizar
        </button>
      </section>

      <section v-if="success" class="banner success" role="status">
        <fa :icon="['fas', 'circle-check']" aria-hidden="true" />
        <div><strong>{{ success.title }}</strong><p>{{ success.message }}</p></div>
        <button class="dismiss-button" type="button" aria-label="Fechar confirmação" @click="success = null">
          <fa :icon="['far', 'xmark']" aria-hidden="true" />
        </button>
      </section>

      <section v-if="error" class="banner error" role="alert">
        <fa :icon="['fas', 'triangle-exclamation']" aria-hidden="true" />
        <div><strong>Não foi possível carregar a mesa.</strong><p>{{ error }}</p></div>
        <button class="button secondary" type="button" @click="loadRequests">Tentar novamente</button>
      </section>

      <section class="kanban" aria-label="Solicitações de desconto por situação" :aria-busy="loading">
        <article v-for="column in statusColumns" :key="column.key" class="kanban-column" :class="`status-${column.key}`">
          <header>
            <span class="status-icon"><fa :icon="['fas', column.icon]" aria-hidden="true" /></span>
            <div><h2>{{ column.label }}</h2><p>{{ column.description }}</p></div>
            <strong :aria-label="`${totalFor(column.key)} solicitações`">{{ totalFor(column.key) }}</strong>
          </header>

          <div v-if="loading" class="cards" aria-hidden="true">
            <span v-for="item in 3" :key="item" class="request-card skeleton"></span>
          </div>
          <div v-else-if="requestsFor(column.key).length" class="cards">
            <component
              :is="column.key === 'pending' ? 'button' : 'article'"
              v-for="request in requestsFor(column.key)"
              :key="request.id"
              class="request-card"
              :type="column.key === 'pending' ? 'button' : undefined"
              @click="column.key === 'pending' && openRequest(request.id)"
            >
              <div class="card-heading">
                <div><strong>{{ request.student_name }}</strong><span>{{ request.student_rm ? `RM ${request.student_rm}` : `Período ${request.period_year}` }}</span></div>
              </div>
              <dl>
                <div><dt>Unidade</dt><dd>{{ request.unit || 'A conciliar' }}</dd></div>
                <div><dt>Turma</dt><dd>{{ request.reconciliation_status ? 'Aguardando conciliação' : `${request.series} · ${request.class}` }}</dd></div>
                <div><dt>Incidência</dt><dd>{{ request.discount_type_label }}</dd></div>
              </dl>
              <div class="card-footer">
                <span>{{ request.motive_label }}</span>
                <time :datetime="request.created_at">{{ formatDateTime(request.created_at) }}</time>
              </div>
              <span v-if="column.key === 'pending'" class="open-label">{{ request.reconciliation_status ? 'Conciliar matrícula' : 'Analisar solicitação' }} <fa :icon="['far', 'arrow-right']" aria-hidden="true" /></span>
              <span v-else-if="request.decision_id" class="decision-summary">
                Parecer {{ request.score }} pts · {{ formatPercent(request.final_pp) }} final
              </span>
            </component>
          </div>
          <div v-else class="empty-column">
            <fa :icon="['fas', column.emptyIcon]" aria-hidden="true" />
            <strong>Nenhuma solicitação {{ column.emptyLabel }}</strong>
            <p>{{ search ? 'Tente ajustar a busca.' : column.emptyText }}</p>
          </div>
        </article>
      </section>

      <p class="board-note" role="note">
        <fa :icon="['fas', 'circle-info']" aria-hidden="true" />
        Os cards mudam de etapa somente quando o parecer é finalizado. Não há movimentação manual entre colunas.
      </p>

      <discount-decision-request-modal
        v-if="manualRequestOpen"
        :rule="rule"
        @close="manualRequestOpen = false"
        @created="handleRequestCreated"
      />
    </main>
  </controller>
</template>

<script>
import DiscountDecisionService from './discountDecision.service'
import DiscountDecisionRequestModal from './DiscountDecisionRequestModal.vue'
import UserService from '@/services/user.service'

export default {
  name: 'DiscountDecisionKanban',
  components: { DiscountDecisionRequestModal },
  data () {
    return {
      loading: true,
      error: null,
      success: null,
      search: '',
      searchTimer: null,
      rule: {},
      columns: { pending: [], approved: [], rejected: [] },
      totals: { pending: 0, approved: 0, rejected: 0 },
      manualRequestOpen: false,
      statusColumns: [
        { key: 'pending', label: 'Pendente', description: 'Aguardando análise', icon: 'clock', emptyIcon: 'clipboard-check', emptyLabel: 'pendente', emptyText: 'Novos pedidos aparecerão aqui.' },
        { key: 'approved', label: 'Aprovado', description: 'Concessão autorizada', icon: 'circle-check', emptyIcon: 'circle-check', emptyLabel: 'aprovada', emptyText: 'Pareceres aprovados aparecerão aqui.' },
        { key: 'rejected', label: 'Recusado', description: 'Concessão não autorizada', icon: 'ban', emptyIcon: 'ban', emptyLabel: 'recusada', emptyText: 'Pareceres recusados aparecerão aqui.' }
      ]
    }
  },
  async mounted () {
    const parameters = DiscountDecisionService.parameters()
      .then(response => { this.rule = response.data })
      .catch(() => {})
    await Promise.all([parameters, this.loadRequests()])
  },
  beforeUnmount () {
    window.clearTimeout(this.searchTimer)
  },
  computed: {
    canEvaluate () { return UserService.filterPermissions('avaliarMesaDecisaoDescontos') },
    canManage () { return UserService.filterPermissions('gerenciarMesaDecisaoDescontos') }
  },
  methods: {
    scheduleLoad () {
      window.clearTimeout(this.searchTimer)
      this.searchTimer = window.setTimeout(this.loadRequests, 300)
    },
    async loadRequests () {
      this.loading = true
      this.error = null
      try {
        const response = await DiscountDecisionService.requests({ search: this.search || undefined })
        this.columns = response.data.columns || { pending: [], approved: [], rejected: [] }
        this.totals = response.data.totals || { pending: 0, approved: 0, rejected: 0 }
      } catch (error) {
        this.error = error.response?.data?.message || error.message || 'Erro inesperado.'
      } finally {
        this.loading = false
      }
    },
    requestsFor (status) { return this.columns[status] || [] },
    totalFor (status) { return this.totals[status] || 0 },
    async handleRequestCreated (result) {
      this.manualRequestOpen = false
      this.success = result.created
        ? { title: 'Solicitação adicionada.', message: 'O pedido foi incluído na coluna Pendente.' }
        : { title: 'Solicitação já existente.', message: 'A matrícula já possui um pedido pendente na mesa.' }
      await this.loadRequests()
    },
    openRequest (requestId) { this.$router.push({ name: 'Mesa de Decisão - Solicitação', params: { requestId } }) },
    formatPercent (value) { return `${Number(value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%` },
    formatDateTime (value) { return value ? new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(value)) : '—' }
  }
}
</script>

<style lang="scss" scoped>
$secondary-text: darken($color-text, 20%);
.kanban-page { color: $color-dev; margin: 0 auto; max-width: 1580px; padding: $mg; width: 100%; }
.page-header { align-items: flex-start; display: flex; gap: $mg; justify-content: space-between; margin-bottom: $mg; h1 { font-size: 24px; margin: 0 0 $hmg_mini; } p { color: $secondary-text; margin: 0; } }
.header-actions { align-items: center; display: flex; flex-wrap: wrap; gap: $hmg_small; justify-content: flex-end; }
.eyebrow { color: $color-primary !important; font-size: $font-size-small; font-weight: 700; letter-spacing: .04em; text-transform: uppercase; }
.rule-badge { background: $color-grey; border: 1px solid $color-component; border-radius: 999px; color: $color-dev; font-size: $font-size-small; font-weight: 700; padding: $hmg_mini $hmg_small; white-space: nowrap; }
.toolbar { align-items: end; background: $color-secondary; border: 1px solid $color-component; border-radius: 16px; display: grid; gap: $hmg_small; grid-template-columns: minmax(260px, 520px) auto; margin-bottom: $mg; padding: $hmg_mid; label { font-weight: 700; grid-column: 1 / -1; margin-bottom: -$hmg_mini; } }
.search-control { align-items: center; border: 1px solid $color-mid-grey; border-radius: 14px; display: flex; min-height: 46px; padding: 0 $hmg_small; svg { color: $color-primary; } input { background: transparent; border: 0; flex: 1; font: inherit; min-width: 0; outline: 0; padding: 0 $hmg_small; } &:focus-within { border-color: $color-primary; box-shadow: 0 0 0 3px rgba($color-primary, .12); } }
.button { align-items: center; border: 1px solid transparent; border-radius: 14px; cursor: pointer; display: inline-flex; font: inherit; font-weight: 700; gap: $hmg_mini; justify-content: center; min-height: 46px; padding: 0 $hmg_mid; &.primary { background: $color-primary; color: $color-secondary; } &.secondary { background: $color-secondary; border-color: $color-mid-grey; color: $color-dev; } &:disabled { cursor: not-allowed; opacity: .55; } &:focus-visible { outline: 3px solid rgba($color-primary, .25); outline-offset: 2px; } }
.kanban { align-items: flex-start; display: grid; gap: $hmg_mid; grid-template-columns: repeat(3, minmax(0, 1fr)); }
.kanban-column { background: $color-grey; border: 1px solid $color-component; border-radius: 16px; min-width: 0; padding: $hmg_small; > header { align-items: center; display: grid; gap: $hmg_small; grid-template-columns: auto 1fr auto; padding: $hmg_mini $hmg_mini $hmg_small; h2 { font-size: 16px; margin: 0; } p { color: $secondary-text; font-size: $font-size-small; margin: 2px 0 0; } > strong { align-items: center; background: $color-secondary; border: 1px solid $color-component; border-radius: 999px; display: inline-flex; height: 30px; justify-content: center; min-width: 30px; padding: 0 $hmg_mini; } } }
.status-icon { align-items: center; background: $color-secondary; border: 1px solid $color-component; border-radius: 10px; display: inline-flex; height: 40px; justify-content: center; width: 40px; }.status-pending .status-icon { color: darken($color-warning, 30%); }.status-approved .status-icon { color: darken($color-success-2, 18%); }.status-rejected .status-icon { color: $color-error-2; }
.cards { display: grid; gap: $hmg_small; }.request-card { background: $color-secondary; border: 1px solid $color-component; border-radius: 14px; color: $color-dev; display: block; font: inherit; min-height: 210px; padding: $hmg_mid; text-align: left; width: 100%; }.status-pending button.request-card { cursor: pointer; transition: border-color .16s ease, transform .16s ease; &:hover { border-color: $color-primary; transform: translateY(-2px); } &:focus-visible { border-color: $color-primary; outline: 3px solid rgba($color-primary, .25); outline-offset: 2px; } }
.card-heading { align-items: flex-start; display: flex; gap: $hmg_small; justify-content: space-between; > div { display: grid; gap: 2px; min-width: 0; strong { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; } span { color: $secondary-text; font-size: $font-size-small; } } }
.request-card dl { border-bottom: 1px solid $color-component; border-top: 1px solid $color-component; display: grid; gap: $hmg_mini; margin: $hmg_small 0; padding: $hmg_small 0; > div { display: flex; gap: $hmg_small; justify-content: space-between; } dt { color: $secondary-text; font-size: $font-size-small; } dd { font-weight: 600; margin: 0; text-align: right; } }.card-footer { align-items: center; color: $secondary-text; display: flex; font-size: $font-size-small; gap: $hmg_small; justify-content: space-between; }.open-label, .decision-summary { color: $color-primary; display: flex; font-size: $font-size-small; font-weight: 700; gap: $hmg_mini; justify-content: flex-end; margin-top: $hmg_small; }.decision-summary { color: $secondary-text; justify-content: flex-start; }
.empty-column { align-items: center; color: $secondary-text; display: flex; flex-direction: column; min-height: 250px; justify-content: center; padding: $mg; text-align: center; svg { font-size: 26px; margin-bottom: $hmg_small; } strong { color: $color-dev; } p { margin: $hmg_mini 0 0; } }
.skeleton { animation: pulse 1.2s ease-in-out infinite; background: $color-component; border-color: transparent; }@keyframes pulse { 50% { opacity: .45; } }
.banner { align-items: flex-start; border: 1px solid; border-radius: 14px; display: flex; gap: $hmg_small; margin-bottom: $hmg_mid; padding: $hmg_small $hmg_mid; .button, .dismiss-button { margin-left: auto; } p { margin: 2px 0 0; } &.error { background: rgba($color-error, .07); border-color: rgba($color-error, .3); color: $color-error-2; } &.success { background: rgba($color-success-2, .08); border-color: rgba($color-success-2, .35); color: darken($color-success-2, 24%); } }
.dismiss-button { background: transparent; border: 0; color: inherit; cursor: pointer; min-height: 40px; min-width: 40px; }
.board-note { align-items: center; color: $secondary-text; display: flex; font-size: $font-size-small; gap: $hmg_mini; margin: $hmg_small 0 0; }
@media (max-width: 1050px) { .kanban { grid-template-columns: 1fr; }.request-card { min-height: auto; } }
@media (max-width: 720px) { .kanban-page { padding: $hmg_small; }.page-header { align-items: stretch; flex-direction: column; }.header-actions { align-items: stretch; flex-direction: column; }.rule-badge { text-align: center; }.toolbar { grid-template-columns: 1fr; }.toolbar label { grid-column: auto; }.banner { align-items: stretch; flex-direction: column; }.banner .button { margin-left: 0; }.card-heading { align-items: stretch; flex-direction: column; } }
@media (prefers-reduced-motion: reduce) { *, *::before, *::after { animation-duration: .01ms !important; scroll-behavior: auto !important; transition-duration: .01ms !important; } }
</style>
